import { CSSProperties } from 'react'
import { PlanType } from 'modules/Const'
import ModalSymphony from 'elements/ModalSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import useScreen from 'Hooks/useScreen'
import { Body2, Subtitle2 } from 'components/shareable/Typography'
import Colors from 'modules/Colors'
import { styx } from 'modules/Utils'

interface CancellationConfirmationModalProps {
    /** Controls modal visibility */
    isOpen: boolean
    /** Callback when modal is closed */
    onClose: () => void
    /** Callback when cancellation is confirmed */
    onConfirm: () => void
    /** Current plan type */
    currentPlan: PlanType
    /** Whether the user is on a grandfathered plan */
    isGrandfathered: boolean
    /** Whether cancellation is in progress */
    isLoading?: boolean
}

const CancellationConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    currentPlan,
    isGrandfathered,
    isLoading = false
}: CancellationConfirmationModalProps) => {
    const { mobileView } = useScreen()

    /**
     * Get benefits that will be lost based on plan type
     */
    const getBenefitsToLose = (plan: PlanType, grandfathered: boolean): string[] => {
        if (grandfathered) {
            switch (plan) {
                case PlanType.LITE:
                    return [
                        'Access to AI campaign creation',
                        'Basic website builder',
                        'Campaign analytics and reporting',
                        'Email support'
                    ]
                case PlanType.PRO:
                    return [
                        'Unlimited AI campaign creation',
                        'Advanced website builder with custom domains',
                        'Advanced analytics and insights',
                        'AI asset generation (images, videos)',
                        'Multiple team members',
                        'Priority support'
                    ]
                case PlanType.TEAM:
                    return [
                        'All Pro features',
                        'Advanced team collaboration',
                        'Team member management',
                        'Advanced reporting and analytics',
                        'Dedicated account manager',
                        'Custom integrations'
                    ]
                default:
                    return ['Access to paid features']
            }
        } else {
            switch (plan) {
                case PlanType.STARTER:
                    return [
                        '5 AI actions per month',
                        'Basic campaign creation',
                        'Website builder access',
                        'Email support'
                    ]
                case PlanType.GROWTH:
                    return [
                        '15 AI actions per month',
                        'Advanced campaign targeting',
                        'Premium website templates',
                        'AI asset generation',
                        'Analytics and insights',
                        'Priority support'
                    ]
                case PlanType.TEAM_V2:
                    return [
                        '50 AI actions per month',
                        'Advanced team features',
                        'Multi-user collaboration',
                        'Advanced analytics',
                        'Custom integrations',
                        'Dedicated support'
                    ]
                default:
                    return ['Access to paid features']
            }
        }
    }

    const benefits = getBenefitsToLose(currentPlan, isGrandfathered)
    const planDisplayName = currentPlan === PlanType.TEAM_V2 ? 'Team' : currentPlan

    return (
        <ModalSymphony
            className="cancellationConfirmationModal"
            isOpen={isOpen}
            onClose={onClose}
            size="medium"
            fullScreen={mobileView}
            title="Are you sure you want to cancel your subscription?"
            subtitle={`Your subscription will be cancelled at the end of your billing cycle.`}
        >
            <div className="cancellationModalContent" style={sty.container}>
                {/* <div className="cancellationModalBenefits" style={sty.benefitsList}>
                    {benefits.map((benefit, index) => (
                        <div key={index} className="cancellationModalBenefit" style={sty.benefitItem}>
                            <div style={sty.bulletPoint}>•</div>
                            <Body2 style={sty.benefitText}>{benefit}</Body2>
                        </div>
                    ))}
                </div> */}

                <div className="cancellationModalActions" style={sty.actions}>
                    <ButtonSymphony
                        className="cancellationModalCancelButton"
                        onClick={onClose}
                        variant="outlined"
                        disabled={isLoading}
                        style={sty.cancelButton}
                    >
                        Keep Subscription
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="cancellationModalConfirmButton"
                        onClick={onConfirm}
                        color="red"
                        isLoading={isLoading}
                        disabled={isLoading}
                        loadingText="Canceling..."
                    >
                        Yes, Cancel Subscription
                    </ButtonSymphony>
                </div>
            </div>
        </ModalSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 24
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        textAlign: 'center'
    },
    title: {
        fontWeight: 600,
        color: Colors.black
    },
    subtitle: {
        color: Colors.grey
    },
    benefitsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 0'
    },
    benefitItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8
    },
    bulletPoint: {
        color: Colors.purple,
        fontWeight: 600,
        fontSize: 16,
        lineHeight: '24px',
        minWidth: 8
    },
    benefitText: {
        color: Colors.black,
        flex: 1
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        justifyContent: 'flex-end'
    },
    cancelButton: {
        minWidth: 140
    }
}

export default CancellationConfirmationModal 